import { Component } from '@theme/component';

/**
 * Comparar de MAMU.
 *
 * - `<mamu-compare-tray>` (sections/mamu-comparar-bandeja.liquid, en todas las páginas): activa los botones
 *   `[data-mamu-compare]`, guarda los handles en `localStorage['mamu:compare']` (máximo `data-max`, 3 por defecto),
 *   pinta la bandeja flotante inferior, actualiza `[data-mamu-compare-count]` del header y lanza los toasts.
 * - `<mamu-compare-page>` (sections/mamu-comparar.liquid): arma la tabla con una columna por producto, pidiendo
 *   `/products/<handle>?section_id=mamu-comparar-columna` (ahí viven los metacampos `mamu.*`).
 *
 * Evento que emite: `mamu:compare-change` en `document`, con `detail = { handles, handle, action, source }`.
 */

const STORAGE_KEY = 'mamu:compare';
const DIFF_KEY = 'mamu:compare-diff';
const COLUMN_SECTION = 'mamu-comparar-columna';
const DEFAULT_MAX = 3;

/* -------------------------------------------------------------------------------------------------
 * Textos (los reales vienen de locales/* vía <script id="mamu-i18n">; estos son el respaldo)
 * ----------------------------------------------------------------------------------------------- */

const DEFAULT_STRINGS = {
  'toast.compare_added': 'Agregado a comparar',
  'toast.compare_removed': 'Quitado de comparar',
  'toast.compare_view': 'Ver comparar ({n})',
  'toast.compare_max': 'Máximo {max} productos',
  'tray.title': 'Comparar',
  'tray.count_long': '{n} de {max} para comparar',
  'tray.count_short': '{n} de {max} · toca para ver',
  'tray.count_sheet': '{n} de {max} productos',
  'tray.slot': 'Agrega otro',
  'tray.remove': 'Quitar {title}',
  'tray.go': 'Comparar ({n})',
  'page.count': '{n} de {max}',
  'page.hint_one': 'Agrega otro producto para compararlo.',
};

/** @type {Record<string, string> | null} */
let strings = null;

/**
 * @param {string} key
 * @param {Record<string, string | number>} [vars]
 * @returns {string}
 */
function t(key, vars = {}) {
  if (!strings) {
    strings = { ...DEFAULT_STRINGS };
    try {
      const raw = document.getElementById('mamu-i18n')?.textContent;
      if (raw) Object.assign(strings, JSON.parse(raw));
    } catch {
      /* se queda el respaldo en español */
    }
  }

  let text = strings[key] ?? key;
  for (const [name, value] of Object.entries(vars)) text = text.replaceAll(`{${name}}`, String(value));
  return text;
}

/* -------------------------------------------------------------------------------------------------
 * Almacén (localStorage con respaldo en memoria si el navegador lo bloquea)
 * ----------------------------------------------------------------------------------------------- */

/** @type {string[] | null} */
let memory = null;

/** @type {string[]} */
let handles = [];

/** @returns {number} Máximo de productos (de `data-max` de la bandeja, entre 2 y 4). */
function getMax() {
  const value = Number.parseInt(document.querySelector('mamu-compare-tray')?.getAttribute('data-max') ?? '', 10);
  return Number.isFinite(value) ? Math.min(4, Math.max(2, value)) : DEFAULT_MAX;
}

/**
 * @param {unknown} value
 * @returns {string[]}
 */
function sanitize(value) {
  if (!Array.isArray(value)) return [];

  /** @type {string[]} */
  const out = [];
  for (const handle of value) {
    if (typeof handle === 'string' && handle && handle.length <= 255 && !out.includes(handle)) out.push(handle);
  }
  return out.slice(0, 4);
}

/** @returns {string[]} */
function load() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return sanitize(raw ? JSON.parse(raw) : []).slice(0, getMax());
  } catch {
    return memory ?? [];
  }
}

/** @param {string[]} list */
function save(list) {
  memory = list;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
  } catch {
    /* sin almacenamiento: la lista vive mientras la pestaña esté abierta */
  }
}

/**
 * @param {Record<string, unknown>} detail
 */
function emitChange(detail) {
  document.dispatchEvent(new CustomEvent('mamu:compare-change', { detail: { handles: [...handles], ...detail } }));
}

/**
 * Agrega o quita un handle respetando el máximo.
 *
 * @param {string} handle
 * @returns {'added' | 'removed' | 'full'}
 */
function toggleCompare(handle) {
  if (handles.includes(handle)) {
    removeCompare(handle);
    return 'removed';
  }

  if (handles.length >= getMax()) return 'full';

  handles = [...handles, handle];
  save(handles);
  emitChange({ handle, action: 'added', source: 'local' });
  return 'added';
}

/**
 * @param {string} handle
 * @param {string} [action]
 */
function removeCompare(handle, action = 'removed') {
  if (!handles.includes(handle)) return;

  handles = handles.filter((h) => h !== handle);
  save(handles);
  emitChange({ handle, action, source: 'local' });
}

function clearCompare() {
  if (!handles.length) return;

  handles = [];
  save(handles);
  emitChange({ handle: null, action: 'cleared', source: 'local' });
}

/* -------------------------------------------------------------------------------------------------
 * Datos de cada producto (Section Rendering API)
 * ----------------------------------------------------------------------------------------------- */

/**
 * @typedef {object} Column
 * @property {string} handle
 * @property {string} title
 * @property {string} url
 * @property {string} image - URL de la foto (160 px) o cadena vacía.
 * @property {string} price - "$22,500 contado", "Precio a cotizar"…
 * @property {string} css - CSS de los snippets que usa la columna (precio, telas), para inyectarlo en la página.
 * @property {HTMLElement} el - Columna completa tal como la pintó Liquid (en otro documento; se clona al usarla).
 */

/** @type {Map<string, Promise<Column | null>>} */
const columnRequests = new Map();

/**
 * Pide la columna de un producto. Una petición por handle y por carga de página; `null` si ya no existe.
 *
 * @param {string} handle
 * @returns {Promise<Column | null>}
 */
function requestColumn(handle) {
  let request = columnRequests.get(handle);

  if (!request) {
    const root = /** @type {any} */ (window).Shopify?.routes?.root ?? '/';
    const url = `${root}products/${encodeURIComponent(handle)}?section_id=${COLUMN_SECTION}`;

    request = fetch(url, { headers: { Accept: 'text/html' } })
      .then(async (response) => {
        if (response.status === 404) return null;
        if (!response.ok) throw new Error(`HTTP ${response.status}`);

        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        const el = doc.querySelector('[data-mamu-cmp-col]');
        if (!(el instanceof HTMLElement)) return null;

        return {
          handle,
          title: el.dataset.title ?? handle,
          url: el.dataset.url ?? '',
          image: el.dataset.image ?? '',
          price: el.dataset.price ?? '',
          css: doc.querySelector('style[data-section-stylesheet]')?.textContent ?? '',
          el,
        };
      })
      .catch((error) => {
        // Un fallo de red no se guarda: la siguiente vez se vuelve a pedir.
        columnRequests.delete(handle);
        throw error;
      });

    columnRequests.set(handle, /** @type {Promise<Column | null>} */ (request));
  }

  return /** @type {Promise<Column | null>} */ (request);
}

/** @param {string} href */
function isCompareUrl(href) {
  try {
    const clean = (/** @type {string} */ path) => path.replace(/\/+$/, '');
    return clean(new URL(href, window.location.href).pathname) === clean(window.location.pathname);
  } catch {
    return false;
  }
}

/* -------------------------------------------------------------------------------------------------
 * Bandeja flotante + controlador global
 * ----------------------------------------------------------------------------------------------- */

/**
 * @typedef {object} TrayRefs
 * @property {HTMLElement} panel
 * @property {HTMLButtonElement} expand - Barra compacta (móvil, colapsada).
 * @property {HTMLElement} stack - Miniaturas apiladas de la barra compacta.
 * @property {HTMLElement} barTitle
 * @property {HTMLElement} barSub
 * @property {HTMLButtonElement} collapse - Chevron de la hoja expandida (móvil).
 * @property {HTMLElement} backdrop
 * @property {HTMLElement} count - Texto "n de max productos" de la cabecera de la hoja.
 * @property {HTMLElement} countLong - Texto "n de max para comparar" (escritorio).
 * @property {HTMLElement} items
 * @property {HTMLAnchorElement} go
 *
 * @extends {Component<TrayRefs>}
 */
class MamuCompareTray extends Component {
  requiredRefs = ['panel', 'expand', 'collapse', 'backdrop', 'items', 'go', 'count', 'countLong', 'barTitle', 'barSub', 'stack'];

  /** @type {AbortController | null} */
  #controller = null;

  /** @type {MutationObserver | null} */
  #observer = null;

  /** @type {ResizeObserver | null} */
  #resizer = null;

  /** @type {MediaQueryList | null} */
  #desktop = null;

  /** Para descartar respuestas de una pasada vieja. */
  #run = 0;

  /** Índice del elemento quitado con el teclado, para devolver el foco al vecino. */
  #focusIndex = -1;

  connectedCallback() {
    super.connectedCallback();

    this.#controller?.abort();
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    handles = load();

    // En captura y deteniendo la propagación: el botón vive dentro de tarjetas con enlace y `on:click` de Horizon.
    document.addEventListener('click', this.#onDocumentClick, { capture: true, signal });
    document.addEventListener('keydown', this.#onKeydown, { signal });
    document.addEventListener('mamu:compare-change', this.#onChange, { signal });
    window.addEventListener('storage', this.#onStorage, { signal });
    window.addEventListener('pageshow', this.#onPageShow, { signal });
    this.refs.items.addEventListener('click', this.#onItemsClick, { signal });

    this.#desktop = window.matchMedia('(min-width: 750px)');
    this.#desktop.addEventListener('change', this.#onBreakpoint, { signal });

    // Botones que llegan después (paginación, filtros, vista rápida, Section Rendering).
    this.#observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (!(node instanceof Element)) continue;
          if (node.matches('[data-mamu-compare]') || node.querySelector('[data-mamu-compare]')) {
            this.#syncButtons();
            return;
          }
        }
      }
    });
    this.#observer.observe(document.body, { childList: true, subtree: true });

    // La página deja sitio abajo para que la bandeja no tape contenido.
    this.#resizer = new ResizeObserver(() => this.#updateInset());
    this.#resizer.observe(this.refs.panel);

    this.#render();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller?.abort();
    this.#controller = null;
    this.#observer?.disconnect();
    this.#observer = null;
    this.#resizer?.disconnect();
    this.#resizer = null;
    this.#run++;
    this.#setInset(0);
  }

  /** Abre la hoja de la bandeja (móvil). */
  expand() {
    if (this.#isDesktop() || this.hidden) return;

    this.#setExpanded(true);
    this.refs.collapse.focus();
  }

  /**
   * Cierra la hoja de la bandeja (móvil).
   *
   * @param {Event} [event]
   */
  collapse(event) {
    const restoreFocus = !(event?.target instanceof HTMLElement && event.target === this.refs.backdrop);
    this.#setExpanded(false);
    if (restoreFocus && !this.hidden) this.refs.expand.focus();
  }

  /** "Limpiar": vacía la comparación. */
  clear() {
    clearCompare();
    document.dispatchEvent(
      new CustomEvent('mamu:toast', { detail: { key: 'compare', icon: 'compare', message: t('toast.compare_removed') } })
    );
  }

  /** @returns {boolean} */
  #isDesktop() {
    return this.#desktop?.matches ?? false;
  }

  /** @returns {boolean} */
  #isExpanded() {
    return this.dataset.expanded === 'true';
  }

  /** @param {boolean} expanded */
  #setExpanded(expanded) {
    this.dataset.expanded = String(expanded);
    this.refs.expand.setAttribute('aria-expanded', String(expanded));
    this.refs.backdrop.hidden = !expanded;
    this.#updateInset();
  }

  #onBreakpoint = () => {
    // Al cambiar de móvil a escritorio la hoja vuelve a su estado normal.
    this.#setExpanded(false);
  };

  /**
   * @param {KeyboardEvent} event
   */
  #onKeydown = (event) => {
    if (event.key !== 'Escape' || !this.#isExpanded() || this.#isDesktop()) return;

    event.preventDefault();
    this.collapse();
  };

  /**
   * @param {MouseEvent} event
   */
  #onDocumentClick = (event) => {
    const target = event.target instanceof Element ? event.target.closest('[data-mamu-compare]') : null;
    if (!(target instanceof HTMLElement)) return;

    event.preventDefault();
    event.stopPropagation();

    const handle = target.dataset.handle;
    if (!handle) return;

    const result = toggleCompare(handle);

    /** @type {Record<string, unknown>} */
    let detail;
    if (result === 'added') {
      detail = {
        key: 'compare',
        icon: 'compare',
        message: t('toast.compare_added'),
        actionLabel: t('toast.compare_view', { n: handles.length }),
        actionHref: this.dataset.compareUrl,
      };
    } else if (result === 'removed') {
      detail = { key: 'compare', icon: 'compare', message: t('toast.compare_removed') };
    } else {
      detail = { key: 'compare', icon: 'compare', message: t('toast.compare_max', { max: getMax() }) };
    }

    document.dispatchEvent(new CustomEvent('mamu:toast', { detail }));
  };

  /**
   * Quitar un producto desde la bandeja.
   *
   * @param {MouseEvent} event
   */
  #onItemsClick = (event) => {
    const button = event.target instanceof Element ? event.target.closest('[data-mamu-tray-remove]') : null;
    if (!(button instanceof HTMLElement)) return;

    const handle = button.dataset.handle;
    if (!handle) return;

    this.#focusIndex = handles.indexOf(handle);
    removeCompare(handle);
  };

  /**
   * Otra pestaña cambió la lista.
   *
   * @param {StorageEvent} event
   */
  #onStorage = (event) => {
    if (event.key !== null && event.key !== STORAGE_KEY) return;

    handles = load();
    emitChange({ handle: null, action: 'sync', source: 'storage' });
  };

  /**
   * @param {PageTransitionEvent} event
   */
  #onPageShow = (event) => {
    if (!event.persisted) return;

    handles = load();
    emitChange({ handle: null, action: 'sync', source: 'pageshow' });
  };

  #onChange = () => {
    this.#render();
  };

  /** Botones `[data-mamu-compare]` y contadores del header. */
  #syncButtons() {
    for (const button of document.querySelectorAll('[data-mamu-compare]')) {
      if (!(button instanceof HTMLElement)) continue;

      const pressed = handles.includes(button.dataset.handle ?? '');
      const label = pressed ? button.dataset.labelRemove : button.dataset.labelAdd;

      button.setAttribute('aria-pressed', String(pressed));
      if (label) button.setAttribute('aria-label', label);
    }

    for (const counter of document.querySelectorAll('[data-mamu-compare-count]')) {
      if (!(counter instanceof HTMLElement)) continue;

      counter.textContent = String(handles.length);
      counter.hidden = handles.length === 0;
    }
  }

  /** @param {number} px */
  #setInset(px) {
    document.documentElement.style.setProperty('--mamu-tray-h', `${px}px`);
  }

  /** Reserva abajo el alto de la bandeja (colapsada o de escritorio) para no tapar el pie de página. */
  #updateInset() {
    if (this.hidden) {
      this.#setInset(0);
      return;
    }

    if (this.#isExpanded() && !this.#isDesktop()) return;

    this.#setInset(Math.ceil(this.refs.panel.getBoundingClientRect().height));
  }

  /** Pinta la bandeja con la lista actual. */
  async #render() {
    const run = ++this.#run;
    const max = getMax();
    const total = handles.length;
    const onComparePage = isCompareUrl(this.dataset.compareUrl ?? '');

    this.#syncButtons();

    const show = total > 0 && !onComparePage;
    this.hidden = !show;
    document.documentElement.classList.toggle('mamu-has-tray', show);

    if (!show) {
      this.#setExpanded(false);
      this.#setInset(0);
      return;
    }

    const { go, count, countLong, barTitle, barSub } = this.refs;

    count.textContent = t('tray.count_sheet', { n: total, max });
    countLong.textContent = t('tray.count_long', { n: total, max });
    barTitle.textContent = t('tray.go', { n: total });
    barSub.textContent = t('tray.count_short', { n: total, max });
    go.textContent = t('tray.go', { n: total });

    // Con lo que ya se conoce de cada producto; el resto se pinta como esqueleto y se completa al llegar.
    this.#paint(max);

    const pending = handles.filter((handle) => !this.#known.has(handle));
    if (pending.length === 0) {
      this.#restoreFocus();
      return;
    }

    const results = await Promise.allSettled(pending.map(requestColumn));
    if (run !== this.#run) return;

    results.forEach((result, index) => {
      const handle = pending[index];
      if (!handle || result.status === 'rejected') return;

      if (result.value) {
        this.#known.set(handle, result.value);
      } else {
        // 404: el producto ya no está publicado.
        removeCompare(handle, 'pruned');
      }
    });

    if (run !== this.#run) return;
    this.#paint(max);
    this.#restoreFocus();
  }

  /** @type {Map<string, Column>} */
  #known = new Map();

  /**
   * @param {number} max
   */
  #paint(max) {
    const { items, stack } = this.refs;

    items.replaceChildren();
    stack.replaceChildren();

    for (const handle of handles) {
      const column = this.#known.get(handle);
      const title = column?.title ?? handle.replaceAll('-', ' ');

      const item = document.createElement('li');
      item.className = 'mamu-tray__item';
      item.dataset.handle = handle;
      if (!column) item.dataset.loading = 'true';

      const thumb = document.createElement('span');
      thumb.className = 'mamu-tray__thumb';
      if (column?.image) thumb.append(this.#image(column.image, 128));

      const info = document.createElement('span');
      info.className = 'mamu-tray__info';
      const name = document.createElement('span');
      name.className = 'mamu-tray__name';
      name.textContent = title;
      info.append(name);
      if (column?.price) {
        const price = document.createElement('span');
        price.className = 'mamu-tray__price';
        price.textContent = column.price;
        info.append(price);
      }

      const remove = document.createElement('button');
      remove.type = 'button';
      remove.className = 'mamu-tray__remove';
      remove.dataset.mamuTrayRemove = '';
      remove.dataset.handle = handle;
      remove.setAttribute('aria-label', t('tray.remove', { title }));
      remove.append(this.#cloneIcon('close'));

      item.append(thumb, info, remove);
      items.append(item);

      if (column?.image && stack.childElementCount < 3) {
        const mini = document.createElement('span');
        mini.className = 'mamu-tray__mini';
        mini.append(this.#image(column.image, 96));
        stack.append(mini);
      }
    }

    for (let i = handles.length; i < max; i++) {
      const slot = document.createElement('li');
      slot.className = 'mamu-tray__slot';
      slot.setAttribute('aria-hidden', 'true');
      slot.textContent = t('tray.slot');
      items.append(slot);
    }
  }

  /**
   * @param {string} src
   * @param {number} size
   * @returns {HTMLImageElement}
   */
  #image(src, size) {
    const image = document.createElement('img');
    image.alt = '';
    image.width = size / 2;
    image.height = size / 2;
    image.loading = 'lazy';
    image.decoding = 'async';
    image.src = src;
    return image;
  }

  /**
   * Icono de las plantillas `<template data-mamu-tray-icon>` que pinta Liquid.
   *
   * @param {string} name
   * @returns {Node}
   */
  #cloneIcon(name) {
    const template = this.querySelector(`template[data-mamu-tray-icon="${name}"]`);
    return template instanceof HTMLTemplateElement ? template.content.cloneNode(true) : document.createDocumentFragment();
  }

  /** Tras quitar con el teclado, el foco pasa al vecino (o al enlace "Comparar"). */
  #restoreFocus() {
    if (this.#focusIndex < 0) return;

    const buttons = this.refs.items.querySelectorAll('.mamu-tray__remove');
    const target = buttons[Math.min(this.#focusIndex, buttons.length - 1)] ?? this.refs.go;
    this.#focusIndex = -1;

    if (target instanceof HTMLElement) target.focus();
  }
}

/* -------------------------------------------------------------------------------------------------
 * Página Comparar
 * ----------------------------------------------------------------------------------------------- */

/**
 * @typedef {object} PageRefs
 * @property {HTMLElement} scroll - Contenedor desplazable de la tabla.
 * @property {HTMLButtonElement} [toggle] - Interruptor "Resaltar diferencias".
 * @property {HTMLElement} [count] - "3 de 3" junto al antetítulo.
 * @property {HTMLElement} [hint] - Aviso cuando solo hay un producto.
 * @property {HTMLElement} [empty] - Estado vacío (enfoque tras quitar el último).
 *
 * @extends {Component<PageRefs>}
 */
class MamuComparePage extends Component {
  requiredRefs = ['scroll'];

  /** @type {AbortController | null} */
  #controller = null;

  #run = 0;

  /** Pedir el foco en el primer "Quitar" tras repintar. */
  #refocus = false;

  connectedCallback() {
    super.connectedCallback();

    this.#controller?.abort();
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    if (!handles.length) handles = load();

    let highlight = true;
    try {
      highlight = localStorage.getItem(DIFF_KEY) !== 'off';
    } catch {
      /* se queda activado */
    }
    this.#setHighlight(highlight);

    document.addEventListener('mamu:compare-change', this.#onChange, { signal });
    this.addEventListener('click', this.#onClick, { signal });

    this.#render();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller?.abort();
    this.#controller = null;
    this.#run++;
  }

  /** Interruptor "Resaltar diferencias" (on:click). */
  toggleHighlight() {
    const next = this.dataset.highlight !== 'on';
    this.#setHighlight(next);

    try {
      localStorage.setItem(DIFF_KEY, next ? 'on' : 'off');
    } catch {
      /* sin almacenamiento */
    }
  }

  /** "Limpiar comparación" (on:click). */
  clearAll() {
    clearCompare();
    document.dispatchEvent(
      new CustomEvent('mamu:toast', { detail: { key: 'compare', icon: 'compare', message: t('toast.compare_removed') } })
    );
  }

  /** "Reintentar" tras un error de red (on:click). */
  retry() {
    this.#render();
  }

  /** @param {boolean} on */
  #setHighlight(on) {
    this.dataset.highlight = on ? 'on' : 'off';
    this.refs.toggle?.setAttribute('aria-checked', String(on));
  }

  /** @param {'loading' | 'ready' | 'empty' | 'error'} state */
  #setState(state) {
    this.dataset.state = state;
  }

  /**
   * "Quitar" de una columna.
   *
   * @param {MouseEvent} event
   */
  #onClick = (event) => {
    const button = event.target instanceof Element ? event.target.closest('[data-mamu-cmp-remove]') : null;
    if (!(button instanceof HTMLElement) || !this.contains(button)) return;

    const handle = button.dataset.handle;
    if (!handle) return;

    this.#refocus = true;
    removeCompare(handle);
    document.dispatchEvent(
      new CustomEvent('mamu:toast', { detail: { key: 'compare', icon: 'compare', message: t('toast.compare_removed') } })
    );
  };

  #onChange = () => {
    this.#render();
  };

  async #render() {
    const run = ++this.#run;
    const list = [...handles];
    const { scroll } = this.refs;

    if (list.length === 0) {
      scroll.replaceChildren();
      this.#updateCount(0);
      this.#setState('empty');
      if (this.#refocus) this.refs.empty?.focus();
      this.#refocus = false;
      return;
    }

    if (!scroll.firstElementChild) this.#setState('loading');

    const results = await Promise.allSettled(list.map(requestColumn));
    if (run !== this.#run) return;

    /** @type {Column[]} */
    const columns = [];
    let failed = false;

    results.forEach((result, index) => {
      const handle = list[index];
      if (!handle) return;

      if (result.status === 'rejected') {
        failed = true;
      } else if (result.value) {
        columns.push(result.value);
      } else {
        // 404: el producto ya no está publicado.
        removeCompare(handle, 'pruned');
      }
    });

    if (run !== this.#run) return; // la poda emitió un cambio y ya hay otra pasada en curso

    if (failed && columns.length === 0) {
      this.#setState('error');
      return;
    }

    if (columns.length === 0) return;

    // CSS de la columna (precio, telas): llega con la respuesta de la sección y se inyecta una sola vez.
    if (!this.querySelector('style[data-mamu-cmp-css]')) {
      const css = columns.find((column) => column.css)?.css;
      if (css) {
        const style = document.createElement('style');
        style.dataset.mamuCmpCss = '';
        style.textContent = css;
        this.prepend(style);
      }
    }

    scroll.replaceChildren(this.#buildTable(columns));
    this.#updateCount(columns.length);
    if (this.refs.hint) this.refs.hint.hidden = columns.length !== 1;
    this.#setState('ready');

    if (this.#refocus) {
      this.#refocus = false;
      const first = scroll.querySelector('[data-mamu-cmp-remove]');
      if (first instanceof HTMLElement) first.focus();
    }
  }

  /** @param {number} total */
  #updateCount(total) {
    const { count } = this.refs;
    if (count) count.textContent = total > 0 ? t('page.count', { n: total, max: getMax() }) : '';
  }

  /**
   * Una tabla: encabezado con la foto de cada producto y una fila por característica.
   * Cada columna de Liquid trae sus celdas como `[data-cell]`; los rótulos salen de `data-label` de la primera.
   *
   * @param {Column[]} columns
   * @returns {HTMLTableElement}
   */
  #buildTable(columns) {
    const table = document.createElement('table');
    table.className = 'mamu-cmp__table';
    table.style.setProperty('--mamu-cmp-cols', String(columns.length));

    const caption = document.createElement('caption');
    caption.className = 'mamu-sr-only';
    caption.textContent = this.dataset.caption ?? '';
    table.append(caption);

    const firstColumn = columns[0];
    const keys = [...(firstColumn?.el.querySelectorAll(':scope > [data-cell]') ?? [])]
      .map((cell) => (cell instanceof HTMLElement ? (cell.dataset.cell ?? '') : ''))
      .filter(Boolean);

    /**
     * @param {Column} column
     * @param {string} key
     * @returns {HTMLElement | null}
     */
    const cellOf = (column, key) => {
      for (const cell of column.el.querySelectorAll(':scope > [data-cell]')) {
        if (cell instanceof HTMLElement && cell.dataset.cell === key) return cell;
      }
      return null;
    };

    // Encabezado: esquina vacía + foto, nombre y "Quitar" de cada producto.
    const head = table.createTHead().insertRow();
    const corner = document.createElement('th');
    corner.scope = 'col';
    corner.className = 'mamu-cmp__corner';
    corner.append(this.#srOnly(this.dataset.cornerLabel ?? ''));
    head.append(corner);

    for (const column of columns) {
      const th = document.createElement('th');
      th.scope = 'col';
      th.className = 'mamu-cmp__colhead';
      const photo = cellOf(column, 'foto');
      if (photo) th.append(...this.#adopt(photo));
      head.append(th);
    }

    // Filas.
    const body = table.createTBody();

    for (const key of keys) {
      if (key === 'foto') continue;

      const firstCell = firstColumn ? cellOf(firstColumn, key) : null;
      const row = body.insertRow();
      row.dataset.row = key;

      const th = document.createElement('th');
      th.scope = 'row';
      th.className = 'mamu-cmp__label';

      const label = document.createElement('span');
      label.className = firstCell?.dataset.hideLabel !== undefined ? 'mamu-sr-only' : 'mamu-cmp__label-text';
      label.textContent = firstCell?.dataset.label ?? '';
      th.append(label);

      if (firstCell?.dataset.note) {
        const note = document.createElement('span');
        note.className = 'mamu-cmp__note';
        note.textContent = firstCell.dataset.note;
        th.append(note);
      }
      row.append(th);

      /** @type {Set<string>} */
      const values = new Set();

      for (const column of columns) {
        const cell = cellOf(column, key);
        const td = row.insertCell();
        td.className = 'mamu-cmp__cell';
        td.dataset.cell = key;
        if (cell) {
          td.append(...this.#adopt(cell));
          values.add((cell.dataset.value ?? cell.textContent ?? '').trim().toLowerCase());
        } else {
          td.textContent = '—';
          values.add('');
        }
      }

      // Las filas de datos que difieren entre productos se resaltan (si el interruptor está activo).
      if (key !== 'comprar' && values.size > 1 && columns.length > 1) row.dataset.diff = 'true';
    }

    return table;
  }

  /**
   * Copia los hijos de una celda de la respuesta al documento vivo.
   *
   * @param {HTMLElement} cell
   * @returns {Node[]}
   */
  #adopt(cell) {
    return [...cell.childNodes].map((node) => document.importNode(node, true));
  }

  /**
   * @param {string} text
   * @returns {HTMLElement}
   */
  #srOnly(text) {
    const span = document.createElement('span');
    span.className = 'mamu-sr-only';
    span.textContent = text;
    return span;
  }
}

if (!customElements.get('mamu-compare-tray')) {
  customElements.define('mamu-compare-tray', MamuCompareTray);
}

if (!customElements.get('mamu-compare-page')) {
  customElements.define('mamu-compare-page', MamuComparePage);
}
